import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import cronstrue from 'cronstrue';
import {
  AlertCircle,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  Pencil,
  Play,
  Plus,
  Trash2,
} from 'lucide-react';
import type { ScheduledTaskRow, UpsertScheduleRequest } from '@command-room/shared';
import {
  useSchedule,
  useUpsertSchedule,
  useDeleteSchedule,
  useTriggerSchedule,
  useAgents,
} from '@/api/queries';
import { BlockSkeleton } from '@/components/feedback/Skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { relativeTime } from '@/lib/format';

// ─── Cron helpers ────────────────────────────────────────────────────────────

function humanCron(expr: string | null): string {
  if (!expr) return 'One-shot';
  try {
    return cronstrue.toString(expr);
  } catch {
    return expr;
  }
}

function parseField(field: string, max: number): number[] {
  if (field === '*') return Array.from({ length: max }, (_, i) => i);
  const result: number[] = [];
  for (const part of field.split(',')) {
    if (part.includes('/')) {
      const slashIdx = part.indexOf('/');
      const range = part.slice(0, slashIdx);
      const stepN = parseInt(part.slice(slashIdx + 1), 10) || 1;
      const rangeStr = range === '*' ? `0-${max - 1}` : range;
      const dashIdx = rangeStr.indexOf('-');
      const start = dashIdx >= 0 ? parseInt(rangeStr.slice(0, dashIdx), 10) : 0;
      const end = dashIdx >= 0 ? parseInt(rangeStr.slice(dashIdx + 1), 10) : max - 1;
      for (let i = start; i <= end; i += stepN) result.push(i);
    } else if (part.includes('-')) {
      const dashIdx = part.indexOf('-');
      const start = parseInt(part.slice(0, dashIdx), 10);
      const end = parseInt(part.slice(dashIdx + 1), 10);
      for (let i = start; i <= end; i++) result.push(i);
    } else {
      result.push(parseInt(part, 10));
    }
  }
  return result;
}

function cronMatchesDay(expr: string, date: Date): boolean {
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 5) return false;
  const domField = parts[2] ?? '*';
  const monthField = parts[3] ?? '*';
  const dowField = parts[4] ?? '*';

  const month = date.getMonth() + 1; // 1-12
  const dom = date.getDate();         // 1-31
  const dow = date.getDay();          // 0 (Sun) - 6 (Sat)

  const months = parseField(monthField, 12).map((m) => m === 0 ? 12 : m);
  if (!months.includes(month)) return false;

  const domAll = domField === '*';
  const dowAll = dowField === '*';

  if (domAll && dowAll) return true;
  if (!domAll && !dowAll) {
    const doms = parseField(domField, 32);
    const dows = parseField(dowField, 7).map((d) => d % 7);
    return doms.includes(dom) || dows.includes(dow);
  }
  if (!domAll) return parseField(domField, 32).includes(dom);
  return parseField(dowField, 7).map((d) => d % 7).includes(dow);
}

function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = [];
  const date = new Date(year, month, 1);
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
}

function getMonday(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function getWeekDays(monday: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// Returns specific hours when the task fires at predictable times, or 'recurring'
// when it fires many times a day (every N minutes/hours).
function getCronHours(expr: string): number[] | 'recurring' {
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 2) return 'recurring';
  const hourField = parts[1] ?? '*';
  if (hourField === '*' || hourField.includes('/')) return 'recurring';
  const hours = parseField(hourField, 24);
  if (hours.length > 3) return 'recurring';
  return hours;
}

const TASK_COLORS: [string, string, string, string, string] = [
  'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  'bg-rose-500/15 text-rose-700 dark:text-rose-300',
];

function taskColor(task: ScheduledTaskRow): string {
  let hash = 0;
  for (const ch of task.id) hash = (hash * 31 + ch.charCodeAt(0)) & 0xffff;
  return TASK_COLORS[hash % TASK_COLORS.length] ?? TASK_COLORS[0];
}

// ─── Schedule Dialog ──────────────────────────────────────────────────────────

type FormData = {
  label: string;
  agent_name: string;
  webhook_url: string;
  schedule_type: 'recurring' | 'one_shot';
  cron_expr: string;
  next_run: string;
  enabled: boolean;
  payload_raw: string;
};

function ScheduleDialog({
  initial,
  open,
  onOpenChange,
}: {
  initial?: ScheduledTaskRow;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const upsert = useUpsertSchedule();
  const { data: agents } = useAgents();

  const toLocalDatetime = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      label: initial?.label ?? '',
      agent_name: initial?.agent_name ?? '',
      webhook_url: initial?.webhook_url ?? '',
      schedule_type: initial?.one_shot ? 'one_shot' : 'recurring',
      cron_expr: initial?.cron_expr ?? '0 9 * * 1-5',
      next_run: initial?.next_run ? toLocalDatetime(initial.next_run) : '',
      enabled: initial?.enabled ?? true,
      payload_raw: initial?.payload ? JSON.stringify(initial.payload, null, 2) : '',
    },
  });

  const scheduleType = watch('schedule_type');
  const cronExpr = watch('cron_expr');
  const enabledVal = watch('enabled');

  let cronPreview: string | null = null;
  if (scheduleType === 'recurring' && cronExpr) {
    try {
      cronPreview = cronstrue.toString(cronExpr);
    } catch {
      cronPreview = null;
    }
  }

  function onClose(v: boolean) {
    if (!v) reset();
    onOpenChange(v);
  }

  async function onSubmit(data: FormData) {
    let payload: Record<string, unknown> = {};
    if (data.payload_raw.trim()) {
      try {
        payload = JSON.parse(data.payload_raw) as Record<string, unknown>;
      } catch {
        // ignore invalid JSON — payload is optional
      }
    }

    const body: UpsertScheduleRequest & { id?: string } = {
      id: initial?.id,
      label: data.label,
      agent_name: data.agent_name,
      webhook_url: data.webhook_url,
      one_shot: data.schedule_type === 'one_shot',
      cron_expr: data.schedule_type === 'recurring' ? (data.cron_expr || null) : null,
      next_run: data.schedule_type === 'one_shot' ? new Date(data.next_run).toISOString() : undefined,
      enabled: data.enabled,
      payload,
    };
    await upsert.mutateAsync(body);
    onClose(false);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        title={initial ? 'Edit task' : 'New scheduled task'}
        description="Task will be dispatched to the agent's webhook on schedule."
        className="max-w-lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              {...register('label', { required: true })}
              placeholder="Daily digest"
              className={errors.label ? 'border-destructive' : ''}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Agent</Label>
              <Select
                value={watch('agent_name')}
                onValueChange={(v) => setValue('agent_name', v)}
              >
                {(agents ?? []).map((a) => (
                  <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="webhook_url">Webhook URL</Label>
              <Input
                id="webhook_url"
                {...register('webhook_url', { required: true })}
                placeholder="https://…/webhook/…"
                className={`font-mono text-xs ${errors.webhook_url ? 'border-destructive' : ''}`}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Schedule type</Label>
            <Select
              value={scheduleType}
              onValueChange={(v) => setValue('schedule_type', v as FormData['schedule_type'])}
            >
              <SelectItem value="recurring">Recurring (cron)</SelectItem>
              <SelectItem value="one_shot">One-shot</SelectItem>
            </Select>
          </div>

          {scheduleType === 'recurring' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cron_expr">Cron expression</Label>
              <Input
                id="cron_expr"
                {...register('cron_expr')}
                placeholder="0 9 * * 1-5"
                className="font-mono"
              />
              {cronPreview ? (
                <p className="text-xs text-muted-foreground">{cronPreview}</p>
              ) : cronExpr ? (
                <p className="text-xs text-destructive">Invalid cron expression</p>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="next_run">Run at</Label>
              <Input
                id="next_run"
                type="datetime-local"
                {...register('next_run', { required: scheduleType === 'one_shot' })}
                className={errors.next_run ? 'border-destructive' : ''}
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payload_raw">
              Payload{' '}
              <span className="text-muted-foreground font-normal text-xs">(JSON, optional)</span>
            </Label>
            <Textarea
              id="payload_raw"
              {...register('payload_raw')}
              rows={3}
              className="font-mono text-xs"
              placeholder='{"task": "digest"}'
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch
              checked={enabledVal}
              onCheckedChange={(v) => setValue('enabled', v)}
              id="enabled"
            />
            <Label htmlFor="enabled" className="cursor-pointer">
              {enabledVal ? 'Enabled' : 'Paused'}
            </Label>
          </div>

          {upsert.error && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {(upsert.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── List view ────────────────────────────────────────────────────────────────

function TaskActions({
  task,
  onEdit,
}: {
  task: ScheduledTaskRow;
  onEdit: (t: ScheduledTaskRow) => void;
}) {
  const deleteTask = useDeleteSchedule();
  const trigger = useTriggerSchedule();
  const upsert = useUpsertSchedule();
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function toggleEnabled() {
    await upsert.mutateAsync({ ...task, enabled: !task.enabled });
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="icon"
        title="Trigger now"
        disabled={trigger.isPending}
        onClick={() => trigger.mutate(task.id)}
      >
        <Play className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        title={task.enabled ? 'Pause' : 'Enable'}
        disabled={upsert.isPending}
        onClick={toggleEnabled}
        className="text-xs"
      >
        {task.enabled ? 'Pause' : 'Enable'}
      </Button>
      <Button variant="ghost" size="icon" title="Edit" onClick={() => onEdit(task)}>
        <Pencil className="h-4 w-4" />
      </Button>
      {confirmDelete ? (
        <div className="flex items-center gap-1">
          <Button
            variant="destructive"
            size="sm"
            disabled={deleteTask.isPending}
            onClick={() => deleteTask.mutate(task.id, { onSuccess: () => setConfirmDelete(false) })}
          >
            Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          title="Delete"
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      )}
    </div>
  );
}

function ListView({
  tasks,
  onEdit,
}: {
  tasks: ScheduledTaskRow[];
  onEdit: (t: ScheduledTaskRow) => void;
}) {
  if (tasks.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No scheduled tasks yet.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Task</th>
            <th className="px-4 py-3">Schedule</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 hidden md:table-cell">Next run</th>
            <th className="px-4 py-3 hidden lg:table-cell">Last run</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {tasks.map((task) => (
            <tr key={task.id} className="hover:bg-muted/20">
              <td className="px-4 py-3">
                <p className="font-medium">{task.label}</p>
                <p className="text-xs text-muted-foreground">{task.agent_name}</p>
              </td>
              <td className="px-4 py-3 text-muted-foreground max-w-xs">
                <span className="line-clamp-1">{humanCron(task.cron_expr)}</span>
              </td>
              <td className="px-4 py-3">
                <Badge variant={task.enabled ? 'success' : 'muted'}>
                  {task.enabled ? 'enabled' : 'paused'}
                </Badge>
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap hidden md:table-cell">
                {new Date(task.next_run).toLocaleString()}
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap hidden lg:table-cell">
                <span className="flex items-center gap-1.5">
                  {task.last_run ? relativeTime(task.last_run) : '—'}
                  {task.last_status && task.last_status !== 'ok' && (
                    <Badge
                      variant={
                        task.last_status === 'fired' ? 'muted' : task.last_status === 'skipped' ? 'warning' : 'destructive'
                      }
                      title={task.last_error ?? undefined}
                    >
                      {task.last_status}
                    </Badge>
                  )}
                </span>
              </td>
              <td className="px-4 py-3">
                <TaskActions task={task} onEdit={onEdit} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Calendar view ────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function CalendarView({
  tasks,
  onEdit,
}: {
  tasks: ScheduledTaskRow[];
  onEdit: (t: ScheduledTaskRow) => void;
}) {
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const days = useMemo(() => getDaysInMonth(year, month), [year, month]);
  const firstDow = days[0]?.getDay() ?? 0; // 0 = Sun

  const tasksByDay = useMemo(() => {
    const map = new Map<string, ScheduledTaskRow[]>();

    for (const day of days) {
      const key = day.toDateString();
      const hits: ScheduledTaskRow[] = [];

      for (const task of tasks) {
        if (!task.enabled) continue;

        if (task.one_shot) {
          const runDate = new Date(task.next_run);
          if (
            runDate.getFullYear() === year &&
            runDate.getMonth() === month &&
            runDate.getDate() === day.getDate()
          ) {
            hits.push(task);
          }
        } else if (task.cron_expr) {
          if (cronMatchesDay(task.cron_expr, day)) hits.push(task);
        }
      }

      map.set(key, hits);
    }
    return map;
  }, [days, tasks, year, month]);

  function prevMonth() {
    setViewDate(new Date(year, month - 1, 1));
  }
  function nextMonth() {
    setViewDate(new Date(year, month + 1, 1));
  }

  const todayStr = today.toDateString();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="icon" onClick={prevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-base font-semibold">
          {MONTH_NAMES[month]} {year}
        </h2>
        <Button variant="outline" size="icon" onClick={nextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 bg-muted/30 border-b border-border">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {/* Leading empty cells */}
          {Array.from({ length: firstDow }).map((_, i) => (
            <div key={`empty-${i}`} className="min-h-[80px] bg-muted/10 border-r border-b border-border last:border-r-0" />
          ))}

          {days.map((day, idx) => {
            const key = day.toDateString();
            const dayTasks = tasksByDay.get(key) ?? [];
            const isToday = key === todayStr;
            const col = (firstDow + idx) % 7;

            return (
              <div
                key={key}
                className={`min-h-[80px] p-1.5 flex flex-col gap-1 border-b border-border ${col < 6 ? 'border-r' : ''} ${isToday ? 'bg-primary/5' : ''}`}
              >
                <span
                  className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full self-end ${
                    isToday
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground'
                  }`}
                >
                  {day.getDate()}
                </span>
                <div className="flex flex-col gap-0.5">
                  {dayTasks.slice(0, 3).map((t) => (
                    <button
                      key={t.id}
                      title={`${t.label} (${t.agent_name})`}
                      onClick={() => onEdit(t)}
                      className={`text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate ${taskColor(t)} hover:opacity-80 transition-opacity`}
                    >
                      {t.label}
                    </button>
                  ))}
                  {dayTasks.length > 3 && (
                    <span className="text-[10px] text-muted-foreground px-1">
                      +{dayTasks.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {tasks.filter((t) => t.enabled).map((t) => (
          <div key={t.id} className="flex items-center gap-1.5 text-xs">
            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${taskColor(t).split(' ')[0]}`} />
            <span className="text-muted-foreground">{t.agent_name}:</span>
            <span>{t.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Week view ────────────────────────────────────────────────────────────────

const WEEK_HOURS = Array.from({ length: 19 }, (_, i) => i + 5); // 05:00–23:00

function WeekView({
  tasks,
  onEdit,
}: {
  tasks: ScheduledTaskRow[];
  onEdit: (t: ScheduledTaskRow) => void;
}) {
  const today = new Date();
  const [weekStart, setWeekStart] = useState(() => getMonday(today));

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const weekNum = getISOWeekNumber(weekStart);
  const todayStr = today.toDateString();

  function prevWeek() {
    setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; });
  }
  function nextWeek() {
    setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; });
  }
  function goToday() {
    setWeekStart(getMonday(new Date()));
  }

  // Classify tasks per day: recurring (many times/day) vs specific hours
  const taskMap = useMemo(() => {
    type DayData = { recurring: ScheduledTaskRow[]; byHour: Map<number, ScheduledTaskRow[]> };
    const map = new Map<string, DayData>();

    for (const day of weekDays) {
      const key = day.toDateString();
      const recurring: ScheduledTaskRow[] = [];
      const byHour = new Map<number, ScheduledTaskRow[]>();

      for (const task of tasks) {
        if (!task.enabled) continue;

        if (task.one_shot) {
          const runDate = new Date(task.next_run);
          if (runDate.toDateString() === key) {
            const h = runDate.getHours();
            byHour.set(h, [...(byHour.get(h) ?? []), task]);
          }
          continue;
        }

        if (!task.cron_expr) continue;
        if (!cronMatchesDay(task.cron_expr, day)) continue;

        const hours = getCronHours(task.cron_expr);
        if (hours === 'recurring') {
          recurring.push(task);
        } else {
          for (const h of hours) {
            byHour.set(h, [...(byHour.get(h) ?? []), task]);
          }
        }
      }

      map.set(key, { recurring, byHour });
    }
    return map;
  }, [weekDays, tasks]);

  const monthLabel = useMemo(() => {
    const first = weekDays[0];
    const last = weekDays[6];
    if (!first || !last) return '';
    if (first.getMonth() === last.getMonth()) {
      return `${MONTH_NAMES[first.getMonth()]} ${first.getFullYear()}`;
    }
    return `${MONTH_NAMES[first.getMonth()]} – ${MONTH_NAMES[last.getMonth()]} ${last.getFullYear()}`;
  }, [weekDays]);

  const hasAnyRecurring = useMemo(
    () => weekDays.some((d) => (taskMap.get(d.toDateString())?.recurring.length ?? 0) > 0),
    [weekDays, taskMap],
  );

  return (
    <div className="flex flex-col rounded-lg border border-border overflow-hidden">
      {/* Navigation header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/20">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-muted-foreground">W{weekNum}</span>
          <span className="text-sm font-semibold">{monthLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={prevWeek} className="h-7 w-7">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} className="h-7 text-xs px-2.5">
            Today
          </Button>
          <Button variant="outline" size="icon" onClick={nextWeek} className="h-7 w-7">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Day header row */}
      <div className="grid border-b border-border bg-muted/20" style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}>
        <div /> {/* spacer above time labels */}
        {weekDays.map((day) => {
          const isToday = day.toDateString() === todayStr;
          return (
            <div key={day.toDateString()} className="py-2 text-center border-l border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {WEEKDAYS[day.getDay()]}
              </div>
              <div className={`mx-auto mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
              }`}>
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recurring (all-day) row — only shown when there are recurring tasks this week */}
      {hasAnyRecurring && (
        <div
          className="grid border-b border-border"
          style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}
        >
          <div className="flex items-start justify-end pr-2 pt-1.5">
            <span className="text-[10px] text-muted-foreground leading-none">recurring</span>
          </div>
          {weekDays.map((day) => {
            const recurring = taskMap.get(day.toDateString())?.recurring ?? [];
            const isToday = day.toDateString() === todayStr;
            return (
              <div
                key={day.toDateString()}
                className={`border-l border-border min-h-[28px] p-0.5 flex flex-col gap-0.5 ${isToday ? 'bg-primary/5' : ''}`}
              >
                {recurring.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => onEdit(t)}
                    title={`${t.label} — ${humanCron(t.cron_expr)}`}
                    className={`text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate w-full ${taskColor(t)} hover:opacity-80 transition-opacity`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Time grid */}
      <div>
        {WEEK_HOURS.map((hour) => (
          <div
            key={hour}
            className="grid border-b border-border last:border-b-0"
            style={{ gridTemplateColumns: '56px repeat(7, 1fr)' }}
          >
            {/* Hour label — aligned to top, offset up slightly so it sits on the line */}
            <div className="flex items-start justify-end pr-2 -mt-2 pt-0">
              <span className="text-[10px] text-muted-foreground tabular-nums leading-none">
                {String(hour).padStart(2, '0')}:00
              </span>
            </div>

            {weekDays.map((day) => {
              const tasksAtHour = taskMap.get(day.toDateString())?.byHour.get(hour) ?? [];
              const isToday = day.toDateString() === todayStr;
              return (
                <div
                  key={day.toDateString()}
                  className={`border-l border-border min-h-[48px] p-0.5 flex flex-col gap-0.5 ${isToday ? 'bg-primary/5' : ''}`}
                >
                  {tasksAtHour.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => onEdit(t)}
                      title={`${t.label} (${t.agent_name})`}
                      className={`text-left text-[10px] leading-tight px-1.5 py-1 rounded w-full ${taskColor(t)} hover:opacity-80 transition-opacity`}
                    >
                      <div className="font-medium truncate">{t.label}</div>
                      <div className="opacity-70">{String(hour).padStart(2, '0')}:00</div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SchedulerPage() {
  const { data: tasks, isLoading } = useSchedule();
  const [view, setView] = useState<'list' | 'week' | 'calendar'>('list');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduledTaskRow | undefined>(undefined);

  function openCreate() {
    setEditing(undefined);
    setDialogOpen(true);
  }

  function openEdit(task: ScheduledTaskRow) {
    setEditing(task);
    setDialogOpen(true);
  }

  const allTasks = tasks ?? [];
  const enabledCount = allTasks.filter((t) => t.enabled).length;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Scheduler</h1>
          <p className="text-sm text-muted-foreground">
            {allTasks.length} task{allTasks.length === 1 ? '' : 's'}
            {enabledCount < allTasks.length && (
              <span className="ml-1.5 text-muted-foreground/70">
                · {allTasks.length - enabledCount} paused
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            <button
              onClick={() => setView('list')}
              className={`px-2.5 py-1.5 flex items-center gap-1.5 text-xs transition-colors ${
                view === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground'
              }`}
            >
              <LayoutList className="h-3.5 w-3.5" />
              List
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-2.5 py-1.5 flex items-center gap-1.5 text-xs border-l border-border transition-colors ${
                view === 'week'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground'
              }`}
            >
              <CalendarRange className="h-3.5 w-3.5" />
              Week
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`px-2.5 py-1.5 flex items-center gap-1.5 text-xs border-l border-border transition-colors ${
                view === 'calendar'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground'
              }`}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Month
            </button>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New task
          </Button>
        </div>
      </header>

      {isLoading ? (
        <BlockSkeleton />
      ) : view === 'list' ? (
        <ListView tasks={allTasks} onEdit={openEdit} />
      ) : view === 'week' ? (
        <WeekView tasks={allTasks} onEdit={openEdit} />
      ) : (
        <CalendarView tasks={allTasks} onEdit={openEdit} />
      )}

      <ScheduleDialog
        key={editing?.id ?? 'new'}
        initial={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
